import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { IRemunerationGoal } from 'interfaces/RemunerationGoal';
import {
  IMyKnownError,
  REQUEST_LIFE_CICLE,
  ICreateRequestPayload,
} from 'interfaces/requests';

import { get, post, patch, MICROSERVICES } from 'api';
import { AppDispatch } from 'store';
import { normalizeAndRemoveFields } from 'utils/files';
import { openAlert } from 'modules/alertHandler';

import endpoints from './endpoints';
import { IRemunerationGoalState } from './interfaces';
import { setSelectedRemunerationModeAction } from 'modules/remunerationModeNavigation/actions';
import { listRemunerationGoalAction } from 'modules/remunerationGoalList';
import { REMUNERATION_FORM_MODE } from 'modules/remunerationModeNavigation/constants';
import { parseToInt } from 'utils/texts';
import { remunerationFieldsToRemove } from 'utils/remuneration';

export const showRemunerationGoalAction = createAsyncThunk<
  IRemunerationGoal,
  string,
  { rejectValue: IMyKnownError }
>('showRemunerationGoal', async (payload, { rejectWithValue, dispatch }) => {
  try {
    const result = await get<IRemunerationGoal>(MICROSERVICES.STRATEGIES, {
      url: endpoints.showRemunerationGoal(payload),
    });
    const flowRanges = result.data.flowRanges.map((flowRange, index) => ({
      ...flowRange,
      id: index.toString(),
    }));

    const remuneration = {
      ...result.data,
      id: result.data._id,
      flowRanges,
    };
    dispatch(
      setSelectedRemunerationModeAction(REMUNERATION_FORM_MODE.viewGoal),
    );
    return remuneration;
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: error.response.data.message,
      }),
    );
    return rejectWithValue(error);
  }
});

export const createRemunerationGoalAction = createAsyncThunk<
  void,
  ICreateRequestPayload<IRemunerationGoal>,
  { rejectValue: IMyKnownError; dispatch: AppDispatch }
>(
  'createRemunerationGoal',
  async ({ item, resetFormSubmitting }, { rejectWithValue, dispatch }) => {
    const fieldsToRemove = [];
    if (item.indicator !== 'others') {
      fieldsToRemove.push('indicatorSpecification');
    }
    if (item.indicator === 'commissionValue') {
      fieldsToRemove.push('remunerationReferenceValue');
    }

    const flowRanges = item.flowRanges.map((flowRange) =>
      normalizeAndRemoveFields(Object.assign({}, flowRange), [
        'id',
        'beforeBreakpointValue',
      ]),
    );
    const periodicityMonthSpecification = parseToInt(
      item.periodicityMonthSpecification,
    );
    const parsedRemunerationGoal = {
      ...normalizeAndRemoveFields(
        Object.assign({}, { ...item, periodicityMonthSpecification }),
        remunerationFieldsToRemove(item.periodicityType, fieldsToRemove),
      ),
      flowRanges,
    };
    try {
      await post<IRemunerationGoal>(MICROSERVICES.STRATEGIES, {
        url: endpoints.createRemunerationsGoal(),
        data: parsedRemunerationGoal,
      });
      resetFormSubmitting();
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração criado com sucesso!',
          primaryButtonAction: setSelectedRemunerationModeAction(
            REMUNERATION_FORM_MODE.list,
          ),
        }),
      );
    } catch (error) {
      const errorMessage = error.response.data.message.match('name is already')
        ? 'Já existe Remuneração Meta com esse nome.'
        : 'Erro para criar uma Remuneração Meta';
      dispatch(
        openAlert({
          title: 'Erro!',
          content: errorMessage,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const inactivateRemunerationGoalAction = createAsyncThunk<
  void,
  string,
  { rejectValue: IMyKnownError }
>(
  'inactivateRemunerationGoal',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      await patch<unknown>(MICROSERVICES.STRATEGIES, {
        url: endpoints.inactivateRemunerationGoal(payload),
      });
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração inativado com sucesso!',
          primaryButtonAction: listRemunerationGoalAction({}),
        }),
      );
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content: error.response.data.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

const initialState: IRemunerationGoalState = {
  loading: REQUEST_LIFE_CICLE.IDLE,
  submitting: REQUEST_LIFE_CICLE.IDLE,
  remunerationGoal: {} as IRemunerationGoal,
  errorMessage: '',
};

const remunerationGoalSlice = createSlice({
  name: 'remunerationGoal',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(showRemunerationGoalAction.fulfilled, (state, action) => {
      state.errorMessage = '';
      state.remunerationGoal = action.payload;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(showRemunerationGoalAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(showRemunerationGoalAction.rejected, (state, action) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      state.errorMessage = action.payload?.message || '';
      return state;
    });
    builder.addCase(createRemunerationGoalAction.fulfilled, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(createRemunerationGoalAction.pending, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(createRemunerationGoalAction.rejected, (state, action) => {
      state.submitting = REQUEST_LIFE_CICLE.FAILED;
      state.errorMessage = action.payload?.message || '';
      return state;
    });
    builder.addCase(inactivateRemunerationGoalAction.fulfilled, () => {
      return initialState;
    });
    builder.addCase(inactivateRemunerationGoalAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(
      inactivateRemunerationGoalAction.rejected,
      (state, action) => {
        state.loading = REQUEST_LIFE_CICLE.FAILED;
        state.errorMessage = action.payload?.message || '';
        return state;
      },
    );
  },
});

const { reducer, actions } = remunerationGoalSlice;
export const { reset } = actions;
export default reducer;
